export type BusinessLocationBenefit = {
  code: string;
  value: number;
  productElementId?: string;
  /**
   * Nome legível (ex.: "Chaveiro para Acesso a Empresa"). Quando ausente,
   * o card faz lookup no catálogo via `useBusinessPlanResourcesByCode`.
   */
  displayName?: string;
};

/**
 * Franquia estruturada da cobertura, montada a partir do catálogo de
 * franquias quando o usuário seleciona uma. Enviada no attach como
 * `policyLimitDeductible` (vira `PolicyLimitDeductibleList` no InsureMO),
 * em complemento ao `codeDeductible`/`Franquia`.
 */
export type BusinessLocationPolicyLimitDeductible = {
  id: string;
  code: string;
  minValue: number;
  name: string;
  description?: string;
  /**
   * Grupo de atividade da franquia, vindo do catálogo já filtrado pela
   * atividade selecionada no local de risco; omitido quando genérica.
   */
  activityGroup?: string;
};

export type BusinessLocationCoverage = {
  code: string;
  value: number;
  displayName?: string;
  productElementId?: string;
  codeDeductible?: string;
  planCode?: string;
  benefits?: BusinessLocationBenefit[];
  policyLimitDeductible?: BusinessLocationPolicyLimitDeductible[];
};

export type BusinessLocation = {
  id: string;
  riskPk?: number;
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  activityType: string;
  activityTypeLabel: string;
  constructionType: string;
  constructionTypeLabel: string;
  isLandmarked: boolean;
  insuredObjectType: string;
  insuredObjectTypeLabel: string;
  riskValue: string;
  /** Lucros Cessantes / Despesas Fixas em BRL formatado; vazio = não contratado. */
  businessInterruptionValue?: string;
  /** Solicitação de desconto para a subscrição ('Y' | 'N'); opcional. */
  underwritingDiscountRequestInd?: 'Y' | 'N';
  assistancePackage: string;
  assistancePackageCode?: string;
  questionnaireAnswers: (string | string[])[];
  coveragesPayload?: BusinessLocationCoverage[];
  benefitsPayload?: BusinessLocationBenefit[];
  propertyType?: string;
  propertyClassification?: string;
};

export function formatBusinessLocationSummary(
  location: Pick<
    BusinessLocation,
    'street' | 'number' | 'complement' | 'neighborhood' | 'city' | 'state'
  >
) {
  const firstBlock = [location.street, location.number]
    .filter(Boolean)
    .join(', ');
  const secondBlock = [location.complement, location.neighborhood]
    .filter(Boolean)
    .join(', ');
  const thirdBlock = [location.city, location.state]
    .filter(Boolean)
    .join(' - ');

  return (
    [firstBlock, secondBlock, thirdBlock].filter(Boolean).join(', ') ||
    'Endereço, Número - Complemento, Bairro, Cidade - UF'
  );
}
